import React, { FC } from 'react';

import styled from 'styled-components';

import { ISanityBlockContent } from '@rbi-ctg/menu';
import { SecondaryLink } from 'components/link';
import BlockRenderer, { SanityBlockContentLinkProps } from 'components/sanity-block-renderer';
import noop from 'utils/noop';

interface SanityBlockContentProps {
  /**
   * Center the text
   *
   * @default false
   **/
  centered?: boolean;
  /**
   * The content as SanityBlockContent
   *
   * @default []
   **/
  content: ISanityBlockContent[];
  /**
   * Invert the text i.e. make it white
   *
   * @default false
   **/
  inverted?: boolean;
  /**
   * On link click fires when a link is clicked
   *
   * @default `noop`
   **/
  onLinkClick?: (url: string) => void;
}

export interface TypographyContainerProps {
  centered: boolean;
  inverted: boolean;
}

export const TypographyContainer = styled(BlockRenderer)<TypographyContainerProps>`
  display: flex;
  flex-direction: column;
  align-items: ${p => (p.centered ? 'center' : 'initial')};
  text-align: ${p => (p.centered ? 'center' : 'initial')};
  margin: 0;
  padding: 0;
  color: ${p => (p.inverted ? Styles.color.white : Styles.color.black)};
  font-family: ${Styles.fontFamily.base};
  font-weight: ${Styles.fontWeight.normal};

  strong {
    font-weight: ${Styles.fontWeight.black};
    letter-spacing: -1.82px;
  }
  em {
    font-style: normal;
    color: ${p => (p.inverted ? Styles.color.white : Styles.color.grey.three)};
    letter-spacing: -0.5px;
  }
  h1,
  h2,
  h3,
  h4 {
    margin: 0;
    padding: 0;
    line-height: 1;
    letter-spacing: -0.3px;
    line-height: 0.8;
  }
  h1 {
    font-size: 4rem;
  }
  h2 {
    font-size: 3rem;
    font-weight: ${Styles.fontWeight.normal};
  }
  h3 {
    font-weight: ${Styles.fontWeight.normal};
  }
  h4 {
    font-weight: ${Styles.fontWeight.heavy};
  }
`;

export const LinkText = styled.span<TypographyContainerProps>`
  color: ${p => (p.inverted ? Styles.color.white : Styles.color.black)};
  text-decoration: underline;
`;

export const SanityBlockContent: FC<SanityBlockContentProps> = ({
  centered = false,
  inverted = false,
  content = [],
  onLinkClick = noop,
  ...rest
}) => {
  const linkSerializers = {
    marks: {
      link: ({ children = [], mark = {} }: SanityBlockContentLinkProps) => {
        const [linkText] = children;
        const { href } = mark;
        if (!href || !linkText) {
          return null;
        }
        const isInternalLink = href?.startsWith('/');
        const onClick = () => onLinkClick(href ?? '');

        const Text = (
          <LinkText inverted={inverted} centered={centered}>
            {linkText}
          </LinkText>
        );
        return isInternalLink ? (
          <SecondaryLink to={href} onClick={onClick}>
            {Text}
          </SecondaryLink>
        ) : (
          <a href={href} onClick={onClick} target="_blank" rel="noreferrer">
            {Text}
          </a>
        );
      },
    },
  };

  return (
    <TypographyContainer
      centered={centered}
      content={content}
      inverted={inverted}
      serializers={linkSerializers}
      {...rest}
    />
  );
};

export default SanityBlockContent;
